//--------------------------------------------------------------------------------------------------Imports
import { Button, Col, Input } from "antd";
import { ConfigurableService, RequestBitacoraEvento, ResponsePackageActivation } from "../../../types/common";
import MarkdownPreviewer from "../../../common/MarkdownPreviewer";
import { useCallback, useEffect, useState } from "react";
import { StyleSheet } from "../../../StyleSheet";
import { eventLog, postGenerateAllTokens, sendVerificationCode } from "../../../methods/Methods";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../stores/store";
import { getGeneralData } from "../../../stores/slices/generalDataSlice";
import { clearAlert, setAlert, showAlert } from "../../../stores/slices/alertSlice";
import _ from "lodash";

//-------------------------------------------------------------------------------------------------- Interface
interface ActivateServicesComponentProps {
    activation: ResponsePackageActivation;
    dataConf: {
        dataList: ConfigurableService[];
    };
};

//-------------------------------------------------------------------------------------------------- Component
const ActivateServicesComponent = (props: ActivateServicesComponentProps) => {
    const [state, setState] = useState<number>(0);
    const { activation, dataConf } = props;
    const dispatch = useDispatch<AppDispatch>();
    const { selectedContract } = useSelector(getGeneralData);
    const [clientId, setClientId] = useState<string>("");
    const [requiresVerification, setRequiresVerification] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [activateDisabled, setActivateDisabled] = useState<boolean>(false);
    const [nexteDisabled, setNexteDisabled] = useState<boolean>(false);

    const dafServices = dataConf.dataList.filter(service =>
        service.name.startsWith("DAF_ACTIVATION_SERVICE")
    );
    const dafServicesMap = Object.fromEntries(
        dafServices.map(service => [service.name, service.enabled])
    );

    // Determina si requiere verificación
    const shouldVerify = useCallback(() => {
        const serviceType = activation.serviceType?.toUpperCase();
        const requiredServiceKey = `DAF_ACTIVATION_SERVICE_${serviceType}`;
        return (
            ["AMAZON", "NETFLIX", "DISNEY"].includes(serviceType) &&
            dafServicesMap[requiredServiceKey] === true
        );
    }, [activation.serviceType, dafServicesMap]);


    const handleFinalize = useCallback(() => {
        dispatch(showAlert(false));
        dispatch(clearAlert());
    }, [dispatch]);

    const sendVerificationCodeToMega = useCallback(async (service: string) => {
        let error = false;
        let message = "";
        let show = false;
        setNexteDisabled(true);
        await sendVerificationCode(clientId, "activacion", service)
            .then((response) => {
                if (response.data[0]) {
                    if (response.data[0].estatus && _.isEqual(response.data[0].estatus, "true")) {
                        setIsCodeSent(true);
                        message = "Ha sido enviado un código de verificación para la activación del servicio vía SMS.\nFavor de confirmarlo en el siguiente paso."
                    } else if (response.data[0].codigo === "2") {
                        error = true;
                        message = "Ha ocurrido un error en el envío de su código de verificación vía SMS, intentarlo más tarde.";
                    } else if (response.data[0].codigo === "3") {
                        error = true;
                        message = "Ha llegado a su límite de intentos de activación en el día, intentarlo el día de mañana.";
                    } else {
                        error = true;
                        message = "Ha ocurrido un error al generar su código de verificación, intentarlo más tarde.";
                    }
                    show = true;
                    setNexteDisabled(false);
                }
            })
            .catch(() => {
                error = true;
                message = "Ha ocurrido un error al generar su código de verificación, intentarlo más tarde.";
                show = true;
                setNexteDisabled(false);
            });

        dispatch(setAlert({
            show: show,
            title: "¡Estimado Usuario!",
            message: message,
            type: (error) ? "ERROR" : "SUCCESS",
            onFinalize: handleFinalize
        }));
    }, [clientId, dispatch, handleFinalize]);

    useEffect(() => {
        if (shouldVerify() && !isCodeSent) {
            setRequiresVerification(true);
        }
    }, [shouldVerify, isCodeSent]);

    const handleActivateClick = async () => {
        if (requiresVerification && !isCodeSent && state === 0) {
            await sendVerificationCodeToMega(activation.serviceType);
            setState(1)
            return;
        }

        if (requiresVerification && state === 1 && verificationCode.length < 6) {
            dispatch(setAlert({
                show: true,
                title: "¡Estimado Usuario!",
                message: "Por favor ingresa el código de verificación.",
                type: "ERROR",
                onFinalize: handleFinalize
            }));
            return;
        }

        await onPressActivate(shouldVerify() ? verificationCode : undefined);

    }

    const onPressActivate = useCallback(async (code?: string) => {
        let message = "";
        let success = false;

        let objEventLog: RequestBitacoraEvento = {
            tipoServicio: "",
            servicio: "",
            promocion: "",
            accion: "Activacion",
            correo: ""
        };

        setActivateDisabled(true);
        await postGenerateAllTokens(activation.activationProcess, clientId, code)
            .then((response) => {
                if (response.data.data[0] && response.data.data[0].status === "SUCCESS") {
                    window.open(response.data.data[0].url, "_blank");
                    objEventLog.tipoServicio = activation.serviceType;
                    eventLog(objEventLog, clientId);
                    success = true;
                } else {
                    message = response.data.data[0].errorDescription;
                }
                setActivateDisabled(false);
            })
            .catch(() => {
                message = "No fue posible obtener el url de activación, intente más tarde.";
                setActivateDisabled(false);
            });

        if (!success) {
            dispatch(setAlert({
                show: true,
                title: "¡Estimado Usuario!",
                message: message,
                type: "ERROR",
                onFinalize: handleFinalize
            }));
        }
    }, [clientId, dispatch, handleFinalize, activation]);

    useEffect(() => {
        if (selectedContract) {
            setClientId(selectedContract.sucursal + selectedContract.suscriptor);
        }
    }, [selectedContract, setClientId]);

    const renderStateZero = () => (
        <>
            <Col span={18} style={styles.mdContainer}>
                <MarkdownPreviewer
                    h1Style={{
                        fontSize: 15,
                        color: "#000000",
                        fontWeight: 700
                    }}
                    h2Style={{
                        fontSize: 12,
                        color: "#707070",
                        fontWeight: 500
                    }}
                    h3Style={{
                        fontSize: 12,
                        color: "#000000",
                        margin: 0,
                        padding: 0,
                        fontWeight: 500
                    }}
                    h4Style={{
                        fontSize: 12,
                        color: "#000000",
                        margin: 0,
                        padding: 0,
                        fontWeight: 500
                    }}
                    textStyle={{
                        fontSize: 10,
                        color: "#000000",
                        margin: 0,
                        padding: 0,
                    }}
                    linkStyle={{
                        fontSize: 12,
                        color: "#000000",
                        textDecoration: "underline",
                    }}
                    mdSource={activation.mdContent}
                />
            </Col>
            <Col span={6} style={styles.buttonContainer}>
                <Button
                    style={styles.buttonStyle}
                    onClick={handleActivateClick}
                    disabled={nexteDisabled}
                >
                    {requiresVerification ? "Continuar" : activation.buttonTitle}
                </Button>
            </Col>
        </>
    );

    const renderStateOne = () => (
        <>
            <Col span={18} style={styles.mdContainer}>
                <Input
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ""))}
                    maxLength={6}
                    placeholder="XXXXXX"
                    style={{ textAlign: "center", marginBottom: 20 }}
                />
            </Col>
            <Col span={6} style={styles.buttonContainer}>
                <Button
                    style={{ ...styles.buttonStyle, marginBottom: 20, marginTop: 0 }}
                    onClick={handleActivateClick}
                    disabled={activateDisabled}
                >
                    {activation.buttonTitle}
                </Button>
            </Col>
        </>
    );


    return ((state === 0) ? (
        renderStateZero()
    ) : ((state === 1) ? (
        renderStateOne()
    ) : null));
};

//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
    mdContainer: {
        paddingRight: 10,
    },
    buttonContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    buttonStyle: {
        height: 30,
        fontSize: 10,
        fontWeight: 600,
        padding: "0px 10px 0px 10px",
        borderRadius: 20,
        border: "none",
        marginBottom: 10,
        marginTop: 10,
        color: "#ffffff",
        backgroundColor: "#0055c2",
    },
});

//-------------------------------------------------------------------------------------------------- Exports
export default ActivateServicesComponent;
